import React, { useMemo } from "react";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { useIntl } from "react-intl";
import { cmmsTypeScale, getAssetHierarchyPalette, getStatLabelSx, getStatisticTileSx } from "../../cmms/assetHierarchyStyles";
import { getTypeBadge } from "../workOrderEnums";
import { getWorkOrderTypeCounts } from "../workOrderTypeCounts";
import messages from "../messages";

/*
 * What kind of work the register is made of, in the page header.
 *
 * Answering "how many of these are preventive maintenance" used to mean setting the type
 * dropdown three times and reading Total off each answer. This says it once, before a row is
 * read.
 *
 * Built on WorkOrderPriorityTile — same tile shape, same unfiltered source, same disappearing
 * act when there is nothing to say. It differs in the one place that matters: that tile counts
 * only open orders, this one counts everything raised. See workOrderTypeCounts.js for why.
 *
 * That difference is exactly what the title has to carry. Three tiles now sit in a row over three
 * different populations — what is on screen, what is open, and everything ever raised — and
 * "All by type" beside "Open by priority" is what stops the second one's "open" being read onto
 * the third.
 */
export default function WorkOrderTypeTile({ workOrders }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  const counts = useMemo(() => getWorkOrderTypeCounts(workOrders), [workOrders]);

  // An empty register has no distribution to show. Returning null rather than an empty tile is
  // what keeps a new customer's header down to the Total beside the title.
  if (counts.length === 0) return null;

  return (
    <Box sx={getStatisticTileSx(cmmsColors)}>
      <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
        {intl.formatMessage(messages.typeCountsTitle)}
      </Typography>

      {/* The chips sit where the Total tile puts its number, so the tiles line up down the page. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
        {counts.map(({ workOrderType, count }) => {
          // The same helper the grid's type column renders with, so a Breakdown chip here is the
          // same red as the Breakdown badge in the row below it.
          const badge = getTypeBadge(workOrderType, intl);

          return (
            <Chip
              key={workOrderType}
              size="small"
              label={intl.formatMessage(messages.typeCountsCount, {
                count,
                type: badge.label,
              })}
              sx={{ backgroundColor: badge.bg, color: badge.text, fontWeight: 700, height: 22 }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
